import { lazy, type ComponentType } from 'react';
import { ROUTES } from '../../shared/constants';

/**
 * Route Configuration
 * 
 * Centralized route definitions with lazy loading for code splitting.
 * This reduces initial bundle size and improves load performance.
 */

// Lazy-loaded page components
const OnboardingPage = lazy(() => import('../../features/onboarding/components/OnboardingPage'));
const CustomerDetailsPage = lazy(() => import('../../features/onboarding/components/CustomerDetailsPage'));
const ProfilePage = lazy(() => import('../../features/profile/ProfilePage'));
const AdminPage = lazy(() => import('../../features/admin/components/AdminPage'));
const AddUserPage = lazy(() => import('../../features/admin/components/AddUserPage'));
const AllUsersPage = lazy(() => import('../../features/admin/components/AllUsersPage'));
const UserDetailPage = lazy(() => import('../../features/admin/components/UserDetailPage'));
const SettingsPage = lazy(() => import('../../features/settings/SettingsPage').then(module => ({ default: module.SettingsPage })));
const ModuleA = lazy(() => import('../../features/moduleA/components/ModuleA').then(module => ({ default: module.ModuleA })));

// Auth pages (not lazy loaded as they're needed immediately)
const AuthCallback = lazy(() => import('../../routes/AuthCallback').then(module => ({ default: module.AuthCallback })));
const PostSignout = lazy(() => import('../../routes/PostSignout').then(module => ({ default: module.PostSignout })));

export interface RouteConfig {
  path: string;
  element: ComponentType<any>;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
}

/**
 * Public routes - accessible without authentication
 */
export const publicRoutes: RouteConfig[] = [
  {
    path: ROUTES.AUTH,
    element: AuthCallback,
    requiresAuth: false,
  },
  {
    path: ROUTES.POST_SIGNOUT,
    element: PostSignout,
    requiresAuth: false,
  },
];

/**
 * Private routes - require authentication
 * These routes are wrapped in ProtectedRoute and AccessControlWrapper
 */
export const privateRoutes: RouteConfig[] = [
  {
    path: '/',
    element: OnboardingPage,
    requiresAuth: true,
  },
  {
    path: 'details/:customerId',
    element: CustomerDetailsPage,
    requiresAuth: true,
  },
  {
    path: 'moduleA',
    element: ModuleA,
    requiresAuth: true,
  },
  {
    path: 'profile',
    element: ProfilePage,
    requiresAuth: true,
  },
  {
    path: 'settings',
    element: SettingsPage,
    requiresAuth: true,
  },
  {
    path: 'admin',
    element: AdminPage,
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: 'admin/add-user',
    element: AddUserPage,
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: 'admin/all-users',
    element: AllUsersPage,
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: 'admin/user/:userEmail',
    element: UserDetailPage,
    requiresAuth: true,
    requiresAdmin: true,
  },
];

/**
 * All routes combined
 */
export const allRoutes = [...publicRoutes, ...privateRoutes];
